import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import produce from 'immer';
import {AccessType, ConnectionStatus, Role, ViewportTab} from '@app/modules/shared/data/constants';
import {User} from '@app/models/vcr-entity/user';
import {Message, MessageType} from '@app/models/message';
import {UtilService} from '@app/modules/shared/services/util.service';

export interface RemoteUserInfoStateModel {
  usersInfoById: { [key: string]: RemoteUserAdditionalInfoStateModel };
}


export class RemoteUserAdditionalInfoStateModel {
  id: number;
  identity: string; /* twilio identity */
  name: string;
  role: Role;
  accessType: AccessType;
  displayName: string;
  firstName: string;
  lastName: string;
  isGuest: boolean;
  personalColor: string;
  roomConnectionStatus: ConnectionStatus;
  isInFullscreenMode: boolean;
  isHandRisen: boolean;
  networkQualityLevel: number;
  isDominantSpeaker: boolean;
  audio: { isMuted: boolean; isProvided: boolean; isLocalUserSubscribed: boolean; voiceLevel: number };
  video: { isMuted: boolean; isProvided: boolean };
  currentSelectedTab: ViewportTab;
  nickname: string;
  isClassParticipant: boolean;
  isUserConnectedDuringSession: boolean;
  orderIndex: number;
  hasUnansweredMessage: boolean;
  stickers: string [];
  connectionTimestamp: number;
  messages: Message [];
  classWideMessage: Message [];
  limitedMessages: Message [];
  doesMessageLengthExceededLimit: boolean;
  correctAnswersCount: number;
  isMessageBoxCollapsed: boolean;

  constructor(user: User, isUserConnectedDuringSession: boolean) {
    this.id = user.id;
    this.identity = user.identity;
    this.name = user.name;
    this.role = user.role;
    this.accessType = user.accessType;
    this.displayName = UtilService.generateDisplayName(user);
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.isGuest = user.isGuest;
    this.personalColor = user.personalColor;
    this.roomConnectionStatus = ConnectionStatus.OFFLINE;
    this.isInFullscreenMode = false;
    this.isHandRisen = false;
    this.isDominantSpeaker = false;
    this.networkQualityLevel = 0;
    this.audio = {isLocalUserSubscribed: null, isMuted: false, isProvided: false, voiceLevel: 0.5};
    this.video = {isMuted: false, isProvided: false};
    this.currentSelectedTab = ViewportTab.WHITEBOARD;
    this.nickname = user.nickname;
    this.isClassParticipant = false;
    this.isUserConnectedDuringSession = isUserConnectedDuringSession;
    this.orderIndex = -1;
    this.hasUnansweredMessage = false;
    this.stickers = [];
    this.connectionTimestamp = user.connectionTimestamp;
    this.messages = [];
    this.classWideMessage=[];
    this.limitedMessages = [];
    this.doesMessageLengthExceededLimit = false;
    this.correctAnswersCount = 0;
    this.isMessageBoxCollapsed = false;
  }
}

export namespace RemoteUserAction {
  export class AddUser {
    static readonly type = '[RemoteUser] add';

    constructor(public user: RemoteUserAdditionalInfoStateModel) {
    }
  }

  export class LogoutUser {
    static readonly type = '[RemoteUser] logout';

    constructor(public userId: number) {
    }
  }

  export class SetUserConnectionStatus {
    static readonly type = '[RemoteUser] set connection status';

    constructor(public userId: number, public connectionStatus: ConnectionStatus) {
    }
  }

  export class SetUserAccessType {
    static readonly type = '[RemoteUser] set user access type';

    constructor(public userId: number, public accessType: AccessType) {
    }
  }

  export class SetUserIdentity {
    static readonly type = '[RemoteUser] set user identity';

    constructor(public userId: number, public identity: string) {
    }
  }

  export class ResetRemoteUserScoreCounter {
    static readonly type = '[RemoteUser] reset user score';

    constructor(public userId: number) {
    }
  }

  export class ResetAllScoreCounters {
    static readonly type = '[RemoteUser] reset all score counter';

    constructor() {
    }
  }

  export class SetUserVoiceLevel {
    static readonly type = '[RemoteUser] set user voice level';

    constructor(public userId: number, public voiceLevel: number) {
    }
  }

  export class SetUserAudioMute {
    static readonly type = '[RemoteUser] set user audio track mute state';

    constructor(public userId: number, public isMuted: boolean) {
    }
  }

  export class SetUsersAudioMute {
    static readonly type = '[RemoteUser] set all users track mute state';

    constructor(public isMuted: boolean) {
    }
  }

  export class SetUserAudioProvide {
    static readonly type = '[RemoteUser] set user audio track provided state';

    constructor(public userId: number, public isProvided: boolean) {
    }
  }

  export class SetUsersAudioProvide {
    static readonly type = '[RemoteUser] set users audio track provided state';

    constructor(public isProvided: boolean) {
    }
  }

  export class SetUserVideoMute {
    static readonly type = '[RemoteUser] set user video track mute state';

    constructor(public userId: number, public isMuted: boolean) {
    }
  }

  export class SetUsersVideoMute {
    static readonly type = '[RemoteUser] set all users video track mute state';

    constructor(public isMuted: boolean) {
    }
  }

  export class SetUserVideoProvide {
    static readonly type = '[RemoteUser] set user video track provided state';

    constructor(public userId: number, public isProvided: boolean) {
    }
  }

  export class SetUsersVideoProvide {
    static readonly type = '[Remote-User-Info] set users video track provided state';

    constructor(public isProvided: boolean) {
    }
  }

  export class ClearUserMessages {
    static readonly type = '[RemoteUser] clear user messages';

    constructor(public userId: number) {
    }
  }

  export class ClearAllMessages {
    static readonly type = '[RemoteUser] clean all messages';

    constructor() {
    }
  }

  export class SetIsDominantSpeaker {
    static readonly type = '[RemoteUser] set user dominant speaker state';

    constructor(public userId: number, public isDominantSpeaker: boolean) {
    }
  }

  export class SetIsClassParticipant {
    static readonly type = '[RemoteUser] set is user current class participant';

    constructor(public userId: number, public isClassParticipant: boolean) {
    }
  }

  export class SetIsConnectedDuringSession {
    static readonly type = '[RemoteUser] set is user connected during class session';

    constructor(public userId: number, public isUserConnectedDuringSession: boolean) {
    }
  }

  export class SetIsInFullscreenMode {
    static readonly type = '[RemoteUser] set is user in fullscreen mode';

    constructor(public userId: number, public isInFullScreenMode: boolean) {
    }
  }

  export class AddSticker {
    static readonly type = '[RemoteUser] add sticker';

    constructor(public userId: number, public sticker: string) {
    }
  }

  export class SetStickers {
    static readonly type = '[RemoteUser] set stickers';

    constructor(public userId: number, public stickers: string[]) {
    }
  }

  export class AddMessageToRemoteUser {
    static readonly type = '[RemoteUser] add massage';

    constructor(public userId: number, public message: Message) {
    }
  }

  export class AddClassWideMessageToRemoteUser {
    static readonly type = '[RemoteUser] add class wide massage';

    constructor(public userId: number, public classWideMessage: Message) {
    }
  }

  export class SetHasUnansweredMessage {
    static readonly type = '[RemoteUser] set has user unanswered message';

    constructor(public userId: number, public hasUnansweredMessage: boolean) {
    }
  }

  export class SetCollapseMessagesBox {
    static readonly type = '[RemoteUser] set collapse user message box';

    constructor(public userId: number, public isMessageBoxCollapsed: boolean) {
    }
  }

  export class SetCorrectAnswer {
    static readonly type = '[RemoteUser] set correct answer';

    constructor(public userId: number, public score: number) {
    }
  }

  export class RiseHand {
    static readonly type = '[RemoteUser] set remote-user rise hand';

    constructor(public userId: number, public isRisen: boolean) {
    }
  }

  export class RemoveSticker {
    static readonly type = '[RemoteUser] remove sticker';

    constructor(public userId: number) {
    }
  }

  export class SetStudentsCurrentTab {
    static readonly type = '[RemoteUser] set all students current selected tab';

    constructor(public tabIndex: ViewportTab) {
    }
  }

  export class SetStudentCurrentTab {
    static readonly type = '[RemoteUser] set student current selected tab';

    constructor(public userId: number, public tabIndex: ViewportTab) {
    }
  }

  export class SetNickname {
    static readonly type = '[RemoteUser] set user nickname';

    constructor(public userId: number, public nickname: string) {
    }
  }
}

@State<RemoteUserInfoStateModel>({
  name: RemoteUsersInfoState.storeName,
  defaults: RemoteUsersInfoState.defaults
})

@Injectable()
export class RemoteUsersInfoState {
  static readonly storeName = 'remoteUsersAdditionalInfo';
  static readonly defaults: RemoteUserInfoStateModel = {
    usersInfoById: {},
  };

  @Selector()
  static getUsersInfo(state: RemoteUserInfoStateModel) {
    return state.usersInfoById;
  }

  @Action(RemoteUserAction.AddUser)
  add(ctx: StateContext<RemoteUserInfoStateModel>, {user}: RemoteUserAction.AddUser) {
    ctx.setState(produce((state) => {
      if (!state.usersInfoById[user.id]) {
        state.usersInfoById[user.id] = user;
      }
    }));
  }

  @Action(RemoteUserAction.LogoutUser)
  logout(ctx: StateContext<RemoteUserInfoStateModel>, {userId}: RemoteUserAction.LogoutUser) {
    //todo need to revisit
    ctx.setState(produce((state) => {
      state.usersInfoById[userId].roomConnectionStatus = ConnectionStatus.OFFLINE;
      state.usersInfoById[userId].isUserConnectedDuringSession = false;
    }));
  }

  @Action(RemoteUserAction.SetUserConnectionStatus)
  toggleUserConnectToTheRoom(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserConnectionStatus) {
    if (ctx.getState().usersInfoById[info.userId]) {
      ctx.setState(produce((state) => {
        state.usersInfoById[info.userId].roomConnectionStatus = info.connectionStatus;
      }));
    } else {
      console.warn('user dose not exist !!!');
    }
  }

  @Action(RemoteUserAction.SetUserAccessType)
  setUserAccessType(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserAccessType) {
    if (ctx.getState().usersInfoById[info.userId]) {
      ctx.setState(produce((state) => {
        state.usersInfoById[info.userId].accessType = info.accessType;
      }));
    } else {
      console.warn('user does not exist !!!');
    }
  }

  @Action(RemoteUserAction.SetUserIdentity)
  setUserIdentity(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserIdentity) {
    if (ctx.getState().usersInfoById[info.userId]) {
      ctx.setState(produce((state) => {
        state.usersInfoById[info.userId].identity = info.identity;
      }));
    } else {
      console.warn('user does not exist !!!');
    }
  }

  @Action(RemoteUserAction.SetUserAudioMute)
  setUserAudioTrackMuteState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserAudioMute) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[info.userId]) {
        state.usersInfoById[info.userId].audio.isMuted = info.isMuted;
      }
    }));
  }

  @Action(RemoteUserAction.SetUserVoiceLevel)
  setUserVoiceLevel(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserVoiceLevel) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[info.userId]) {
        state.usersInfoById[info.userId].audio.voiceLevel = info.voiceLevel;
      }
    }));
  }

  @Action(RemoteUserAction.SetUserAudioProvide)
  setUserAudioTrackProvidedState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserAudioProvide) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].audio.isProvided = info.isProvided;
    }));
  }

  @Action(RemoteUserAction.SetUsersAudioProvide)
  setUsersAudioTrackProvidedState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUsersAudioProvide) {
    ctx.setState(produce((state) => {
      for (const id in state.usersInfoById) {
        if (state.usersInfoById[id]) {
          state.usersInfoById[id].audio.isMuted = info.isProvided;
        }
      }
    }));
  }

  @Action(RemoteUserAction.SetUserVideoMute)
  setUserVideoTrackMuteState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserVideoMute) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].video.isMuted = info.isMuted;
    }));
  }

  @Action(RemoteUserAction.SetUserVideoProvide)
  setUserVideoTrackProvidedState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUserVideoProvide) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].video.isProvided = info.isProvided;
    }));
  }

  @Action(RemoteUserAction.SetUsersVideoProvide)
  setUsersVideoTrackProvidedState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUsersVideoProvide) {
    ctx.setState(produce((state) => {
      for (const id in state.usersInfoById) {
        if (state.usersInfoById[id]) {
          state.usersInfoById[id].video.isProvided = info.isProvided;
        }
      }

    }));
  }

  @Action(RemoteUserAction.SetUsersAudioMute)
  setUsersAudioTrackMuteState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUsersAudioMute) {
    ctx.setState(produce((state) => {
      for (const id in state.usersInfoById) {
        if (state.usersInfoById[id]?.accessType === AccessType.STUDENT) {
          state.usersInfoById[id].audio.isMuted = info.isMuted;
        }
      }
    }));
  }

  @Action(RemoteUserAction.SetUsersVideoMute)
  setUsersVideoTrackMutedState(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetUsersVideoMute) {
    ctx.setState(produce((state) => {
      for (const id in state.usersInfoById) {
        if (state.usersInfoById[id]) {
          state.usersInfoById[id].video.isMuted = info.isMuted;
        }
      }
    }));
  }

  @Action(RemoteUserAction.SetIsDominantSpeaker)
  setIsDominantSpeaker(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetIsDominantSpeaker) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[info.userId]) {
        state.usersInfoById[info.userId].isDominantSpeaker = info.isDominantSpeaker;
      }
    }));
  }

  @Action(RemoteUserAction.SetIsClassParticipant)
  setIsClassParticipant(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetIsClassParticipant) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].isClassParticipant = info.isClassParticipant;
    }));
  }

  @Action(RemoteUserAction.SetIsConnectedDuringSession)
  setIsUserConnectedDuringSession(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetIsConnectedDuringSession) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].isUserConnectedDuringSession = info.isUserConnectedDuringSession;
    }));
  }

  @Action(RemoteUserAction.SetIsInFullscreenMode)
  setIsUserInFullscreenViewMode(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetIsInFullscreenMode) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[info.userId]) {
        state.usersInfoById[info.userId].isInFullscreenMode = info.isInFullScreenMode;
      }
    }));
  }

  @Action(RemoteUserAction.AddSticker)
  addStickerToRemoteUser(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.AddSticker) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[info.userId].stickers.length > 10) {
        state.usersInfoById[info.userId].stickers.splice(0, 1);
      }
      state.usersInfoById[info.userId].stickers.push(info.sticker);
    }));
  }

  @Action(RemoteUserAction.SetStickers)
  setRemoteUserStickers(ctx: StateContext<RemoteUserInfoStateModel>, {userId, stickers}: RemoteUserAction.SetStickers) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[userId]) {
        state.usersInfoById[userId].stickers = stickers;
      }
    }));
  }

  @Action(RemoteUserAction.AddMessageToRemoteUser)
  addMessageToRemoteUser(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.AddMessageToRemoteUser) {
    ctx.setState(produce((state) => {
      const messages = state.usersInfoById[info.userId].messages;
      messages.push(info.message);
      state.usersInfoById[info.userId].messages = messages;
      const messagesCopy: Message[] = JSON.parse(JSON.stringify(messages));
      const messageInfo = this.generateLimitedMessagesArray(messagesCopy);
      state.usersInfoById[info.userId].limitedMessages = messageInfo.array;
      state.usersInfoById[info.userId].doesMessageLengthExceededLimit = messageInfo.doesMessageLengthExceededLimit;


      if (info.message.messageType === MessageType.TO_STAFF) {
        state.usersInfoById[info.userId].hasUnansweredMessage = true;
      } else if (info.message.messageType === MessageType.TO_STUDENT) {
        state.usersInfoById[info.userId].hasUnansweredMessage = false;
      }
    }));
  }

  @Action(RemoteUserAction.AddClassWideMessageToRemoteUser)
  addClassWideMessageToRemoteUser(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.AddClassWideMessageToRemoteUser) {
    ctx.setState(produce((state) => {
      const messages = state.usersInfoById[info.userId].classWideMessage;
      messages.push(info.classWideMessage);
      state.usersInfoById[info.userId].classWideMessage = messages;
      const messagesCopy: Message[] = JSON.parse(JSON.stringify(messages));
      const messageInfo = this.generateLimitedMessagesArray(messagesCopy);
      state.usersInfoById[info.userId].limitedMessages = messageInfo.array;
      state.usersInfoById[info.userId].doesMessageLengthExceededLimit = messageInfo.doesMessageLengthExceededLimit;
    }));
  }

  @Action(RemoteUserAction.SetHasUnansweredMessage)
  toggleHasUserUnansweredMessage(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetHasUnansweredMessage) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].hasUnansweredMessage = info.hasUnansweredMessage;
    }));
  }

  @Action(RemoteUserAction.SetCollapseMessagesBox)
  toggleCollapseMessagesBox(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetCollapseMessagesBox) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].isMessageBoxCollapsed = info.isMessageBoxCollapsed;
    }));
  }

  @Action(RemoteUserAction.RiseHand)
  isHandRaise(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.RiseHand) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].isHandRisen = info.isRisen;
    }));
  }

  @Action(RemoteUserAction.SetCorrectAnswer)
  addCorrectUser(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetCorrectAnswer) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].correctAnswersCount = info.score;
    }));
  }

  @Action(RemoteUserAction.ResetRemoteUserScoreCounter)
  resetScoreCounterRemoteUser(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.ResetRemoteUserScoreCounter) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].correctAnswersCount = 0;
    }));
  }

  @Action(RemoteUserAction.ResetAllScoreCounters)
  resetAllScoreCounter(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.ResetAllScoreCounters) {
    ctx.setState(produce((state) => {
      // eslint-disable-next-line guard-for-in
      for (const userId in state.usersInfoById) {
        state.usersInfoById[userId].correctAnswersCount = 0;
      }
    }));
  }

  @Action(RemoteUserAction.ClearUserMessages)
  clearUserMessages(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.ClearUserMessages) {
    ctx.setState(produce((state) => {
      if (state.usersInfoById[info.userId].messages.length > 0) {
        state.usersInfoById[info.userId].messages = [];
        state.usersInfoById[info.userId].limitedMessages = [];
        state.usersInfoById[info.userId].doesMessageLengthExceededLimit = false;
        state.usersInfoById[info.userId].isMessageBoxCollapsed = false;
        state.usersInfoById[info.userId].hasUnansweredMessage = false;
      }
    }));
  }

  @Action(RemoteUserAction.ClearAllMessages)
  clearAllMessages(ctx: StateContext<RemoteUserInfoStateModel>) {
    ctx.setState(produce((state) => {
      for (const id in state.usersInfoById) {
        if (state.usersInfoById[id]) {
          state.usersInfoById[id].messages = [];
          state.usersInfoById[id].limitedMessages = [];
          state.usersInfoById[id].doesMessageLengthExceededLimit = false;
          state.usersInfoById[id].isMessageBoxCollapsed = false;
          state.usersInfoById[id].hasUnansweredMessage = false;
        }
      }
    }));
  }

  @Action(RemoteUserAction.RemoveSticker)
  removeStickerToRemoteUser(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.RemoveSticker) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].stickers.pop();
    }));
  }

  @Action(RemoteUserAction.SetStudentsCurrentTab)
  toggleStudentsCurrentSelectedTab(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetStudentsCurrentTab) {
    ctx.setState(produce((state) => {
      for (const userId in state.usersInfoById) {
        if (state.usersInfoById[userId].accessType === AccessType.STUDENT) {
          state.usersInfoById[userId].currentSelectedTab = info.tabIndex;
        }
      }
    }));
  }

  @Action(RemoteUserAction.SetStudentCurrentTab)
  toggleStudentCurrentSelectedTab(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetStudentCurrentTab) {
    ctx.setState(produce((state) => {
      state.usersInfoById[info.userId].currentSelectedTab = info.tabIndex;
    }));
  }

  @Action(RemoteUserAction.SetNickname)
  setNickname(ctx: StateContext<RemoteUserInfoStateModel>, info: RemoteUserAction.SetNickname) {
    ctx.setState(produce((state) => {
      // eslint-disable-next-line guard-for-in
        state.usersInfoById[info.userId].nickname = info.nickname;
    }));
  }

  generateLimitedMessagesArray(messages: Message []): { array: Message []; doesMessageLengthExceededLimit: boolean } {
    let totalMessagesLength = 0;
    const limitedArray: Message[] = [];
    const limit = 25;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (totalMessagesLength + messages[i].text.length < limit) {
        limitedArray.push(messages[i]);
        totalMessagesLength += messages[i].text.length + 2;
      } else {
        const messageText: string = messages[i].text.substring(messages[i].text.length - (limit - totalMessagesLength));
        if (messageText !== '') {
          messages[i].text = messageText;
          limitedArray.push(messages[i]);
        }
        return {array: limitedArray.reverse(), doesMessageLengthExceededLimit: true};
      }
    }
    return {array: limitedArray.reverse(), doesMessageLengthExceededLimit: false};
  }
}
